import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from './i18n';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8100/api'
  : '/api';

// Wine name input with suggestions from the catalog (GET /api/wines?q=).
// onSelect receives the catalog wine object ({ id, name, vintage, producer, classification, wine_type, abv, ... }).
export default function WineAutocomplete({ value, onChange, onSelect, placeholder, required = false }) {
  const { t } = useI18n();
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!value || value.trim().length < 2) { setSuggestions([]); return; }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/wines?q=${encodeURIComponent(value)}`, { signal: controller.signal });
        if (res.ok) setSuggestions((await res.json()).slice(0, 8));
      } catch { /* aborted */ }
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [value]);

  useEffect(() => {
    const onDocumentClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, []);

  const pick = (wine) => { onSelect(wine); setOpen(false); setSuggestions([]); };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && highlighted >= 0) { e.preventDefault(); pick(suggestions[highlighted]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div className="autocomplete-wrap" ref={wrapRef}>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlighted(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder || t('catalog.wine_placeholder')}
        required={required}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <ul className="autocomplete-list">
          {suggestions.map((s, i) => (
            <li key={s.id}
                className={i === highlighted ? 'highlighted' : ''}
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}>
              <strong>{s.name}</strong>
              {s.vintage ? <span className="ac-year"> {s.vintage}</span> : ''}
              {s.producer ? <span className="ac-prod"> — {s.producer}</span> : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
